import * as THREE from "three";

const DARK_MODE_COOKIE_NAME = "xmw_dark_mode";

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop().split(";").shift().trim());
  }
  return null;
}

function setCookie(name, value, days = 365) {
  if (typeof document === "undefined") return;
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Class to handle Dark Mode toggling via CSS variables and data-theme attribute.
 * Theme colors are defined in style.css; this controller only toggles the attribute,
 * persists preference to a cookie, and handles non-CSS concerns (Three.js canvas, grid).
 * @category Components
 * @subcategory DarkMode
 */
export class DarkModeController {
  constructor(experience) {
    this.experience = experience;
    this.eventHub = experience.eventHub;
    this.is3dEnabled = false;
    this.isDarkEnabled = false;
    this.darkMode = 0; // 0 = light, 1 = dark
    this.canvasColor = ["white", "#606060"]; // light, dark - for Three.js scene
    this._onEnvironmentSwitched = this.switchModeCanvas.bind(this);

    this.setupEventListeners();
    this.initFromCookie();
  }

  /**
   * Applies the theme to the document root. CSS handles all DOM styling.
   */
  applyTheme(enabled) {
    const root = document.documentElement;
    if (enabled) {
      root.setAttribute("data-theme", "dark");
    } else {
      root.removeAttribute("data-theme");
    }
  }

  /**
   * Initializes dark mode from the cookie. Defers slightly so DOM is ready.
   */
  initFromCookie() {
    const stored = getCookie(DARK_MODE_COOKIE_NAME);
    const enabled = stored === "dark";
    if (!enabled || !this.eventHub) return;

    this.isDarkEnabled = true;
    this.darkMode = 1;
    this.applyTheme(true);

    const finishInit = () => {
      this.switchModeCanvas();
      this.syncDarkModeToggles();
      this.eventHub.emit("dark-switch", { enabled: true });
    };

    requestAnimationFrame(() => {
      finishInit();
      setTimeout(finishInit, 400);
    });
  }

  setupEventListeners() {
    this.eventHub.on("dark-switch", this.handleDarkSwitchEvent.bind(this));
    this.eventHub.on("3d-enabled", (e) => {
      this.is3dEnabled = e.enabled;
      this.switchModeCanvas();
    });
    this.eventHub.on("environment-switched", this._onEnvironmentSwitched);
  }

  cleanup() {
    if (this.eventHub) {
      this.eventHub.off("dark-switch", this.handleDarkSwitchEvent);
      this.eventHub.off("3d-enabled");
      this.eventHub.off("environment-switched", this._onEnvironmentSwitched);
    }
    this.experience = null;
    this.eventHub = null;
  }

  handleDarkSwitchEvent(e) {
    this.isDarkEnabled = e.enabled;
    this.darkMode = e.enabled ? 1 : 0;
    setCookie(DARK_MODE_COOKIE_NAME, e.enabled ? "dark" : "light");
    this.applyTheme(e.enabled);
    this.switchModeCanvas();
    this.syncDarkModeToggles();
  }

  syncDarkModeToggles() {
    const enabled = this.darkMode === 1;
    const headerToggle = document.getElementById("header-dark-mode-toggle");
    if (headerToggle) {
      headerToggle.setAttribute("data-dark", enabled ? "true" : "false");
    }
    const miscCheckbox = document.getElementById("dark-switch");
    if (miscCheckbox) {
      miscCheckbox.checked = enabled;
    }
  }

  /**
   * Updates the Three.js scene background. Cannot be done via CSS.
   */
  switchModeCanvas() {
    const scene = this.experience?.scene;
    if (!scene) return;
    if (this.is3dEnabled) {
      scene.background = new THREE.Color(0xeeeeee);
    } else {
      scene.background = new THREE.Color(this.canvasColor[this.darkMode]);
    }
    this.experience?.renderer?.requestRender?.();
  }
}
